import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  ButtonBase,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  InputAdornment,
  MenuItem,
  Paper,
  Select,
  Slide,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import LockOpenOutlinedIcon from '@mui/icons-material/LockOpenOutlined';
import StarIcon from '@mui/icons-material/Star';
import { toast } from 'react-toastify';
import {
  v2SettingsApi,
  type CatalogEntry,
  type EditableLevel,
  type EffectiveResponse,
  type PolicyMode,
} from './v2-settings-api';
import {
  buildPatchBody as buildPatchBodyPure,
  deleteScopeFor as deleteScopeForPure,
  isPolicyDirty as isPolicyDirtyPure,
  isRowDirty as isRowDirtyPure,
  isValueDirty as isValueDirtyPure,
  NO_POLICY,
  saveAllRows,
  toText,
  validateDraftValue,
  type RowDraft,
} from './customer-override-editor.helpers';
import { deriveReach } from './reach.helpers';
import { StringArrayField } from './string-array-field';

// Re-exported for existing importers (overrides-editor.tsx) — canonical
// definition now lives in v2-settings-api.
export type { EditableLevel };

export interface EntityOverrideEditorProps {
  level: EditableLevel;
  // The entity being edited at this level — the ID PATCH/DELETE target.
  // null means no entity is selected yet (parent's picker is still empty).
  entityId: string | null;
  // The full cascade chain to pass to /effective. Always include the
  // ancestors of `level`: group editor includes customerId + groupId;
  // team editor includes customerId, optional groupId, teamId.
  effectiveParams: { customerId?: string; groupId?: string; teamId?: string };
  // Rendered above the table; this is where the parent's pickers live.
  header: React.ReactNode;
  // Optional message shown when entityId is null — defaults to a generic
  // "pick an entity" prompt, but wrappers can specialize ("Pick a group…").
  emptyStateMessage?: string;
}

// Sentinel for the "save all" in-flight state (overloads savingRow which
// otherwise tracks single-row saves).
const SAVE_ALL_KEY = '__ALL__';

// Per-group status used to dim/annotate sub-setting rows.
type FeatureStatus = 'on' | 'off-unlocked' | 'off-locked' | 'no-master';

// Centralizes API-error shape into one user-facing string.
const extractApiError = (err: any): string =>
  err?.response?.data?.error || err?.response?.data?.message || err?.message || 'unknown error';

// Pretty-print a feature prefix for the group header.
const prettifyPrefix = (prefix: string): string =>
  prefix
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (c) => c.toUpperCase())
    .trim();

// ─── synthetic feature groups ────────────────────────────────────────────────
//
// Groups are normally derived from a setting's first canonical segment, which
// works whenever a feature owns its own prefix. Intake Risk Assessment does
// not: its settings are spread across three unrelated prefixes, so the editor
// listed them under "App", "Safety Hub" and "Intake Risk Assessment" with no
// hint they belong together. Reading an org's IRA config meant knowing which
// three accordions to open, and the Authoracare setup on 2026-10-06 was
// misconfigured (address-only left on, which silently disables the nationwide
// criminal-records search) partly because of it.
//
// A synthetic group collects those prefixes under one heading. `roots` is the
// single source of truth: it decides both membership and, because a feature
// lock is a subtree policy write, which paths "Lock entire feature" writes.
// Renaming the canonicals would be the deeper fix but needs a catalog change in
// both repos plus a migration of every org's stored overrides.
type SyntheticGroup = {
  key: string;
  label: string;
  // Canonical name of the toggle that acts as the group's master switch.
  master: string;
  // Canonical subtree roots owned by this group.
  roots: string[];
  // One-line recipe shown above the group's rows.
  summary?: string;
  // Per-setting plain-English callout. The catalog description says what a
  // setting IS; this says what it has to be SET TO for the feature to work,
  // which is the question someone configuring an org is actually asking.
  notes?: Record<string, { tone: 'required' | 'info'; text: string }>;
};

const SYNTHETIC_GROUPS: SyntheticGroup[] = [
  {
    key: 'IRA',
    label: 'IRA (Intake Risk Assessment)',
    master: 'safetyHub.safetyReport.enhancedToIntakeRiskAssessment.enabled',
    roots: [
      'intakeRiskAssessment',
      'safetyHub.safetyReport.enhancedToIntakeRiskAssessment',
      'app.safetyReport.intakeRiskAssessmentSearch',
    ],
    summary:
      'For name / DOB / address search with criminal records in all states: '
      + 'Safety Report ON, Enhanced to IRA ON, Address-only OFF, Mode = enhanced. '
      + 'The rest tune the results; they do not gate the search.',
    notes: {
      'safetyHub.safetyReport.enabled': {
        tone: 'required',
        text: 'Needs to be ON for name/DOB/address search. It is what makes the section appear in the Safety Hub at all.',
      },
      'safetyHub.safetyReport.enhancedToIntakeRiskAssessment.enabled': {
        tone: 'required',
        text: 'Needs to be ON for name/DOB/address search. OFF leaves a basic crime-data lookup with no name or DOB fields.',
      },
      'safetyHub.safetyReport.enhancedToIntakeRiskAssessment.addressOnly': {
        tone: 'required',
        text: 'Needs to be OFF for name/DOB/address search. ON blanks the name and DOB before the search is sent, which also stops the nationwide criminal-records search from running.',
      },
      'intakeRiskAssessment.mode': {
        tone: 'required',
        text: "Needs to be 'enhanced' for criminal records in all states. 'basic' skips the nationwide records search entirely.",
      },
      'app.safetyReport.intakeRiskAssessmentSearch.enabled': {
        tone: 'info',
        text: 'Mobile app only. Not needed for Safety Hub searches.',
      },
      'intakeRiskAssessment.crimeSensitivityLevel': {
        tone: 'info',
        text: 'Does not gate the search. Sets how much criminal history is shown: 1 = most severe charges only, 10 = unfiltered.',
      },
      'intakeRiskAssessment.searchLimit': {
        tone: 'info',
        text: 'Does not gate the search, but caps it. 0 = unlimited. Once an org reaches the cap further searches fail and nothing in the UI warns beforehand.',
      },
      'intakeRiskAssessment.sexOffender.searchRadiusMeters': {
        tone: 'info',
        text: 'Does not gate the search. Sets how far out nearby sex offenders are listed (1609 m is about 1 mile), so it changes how many appear on a report.',
      },
      'intakeRiskAssessment.autoSaveSearches': {
        tone: 'info',
        text: 'Does not gate the search. OFF means a search only reaches Reports if the user hits Save/Notify.',
      },
      'safetyHub.safetyReport.statewideSearch.enabled': {
        tone: 'info',
        text: 'Optional. Adds a statewide registry sweep toggle to the form, for subjects whose address is unknown.',
      },
      'intakeRiskAssessment.sampleReport.enabled': {
        tone: 'info',
        text: "Keep OFF for real customer orgs. ON makes searches for 'John Doe' or 'Sample Patient' return a fabricated demo report instead of a real one.",
      },
      'intakeRiskAssessment.searchHistory.enabled': {
        tone: 'info',
        text: 'Does not gate the search. ON adds the All Searches tab (admins see every search, other users their own) and the Report to POM Support button, which emails POM Support (not anyone in the org) a reference with no patient details. Intended for enterprise customers.',
      },
    },
  },
];

// Callout for a single setting, if the group that owns it defines one.
const noteFor = (name: string): { tone: 'required' | 'info'; text: string } | null =>
  syntheticGroupFor(name)?.notes?.[name] ?? null;

// A name belongs to a root if it IS the root or sits beneath it. Compared
// segment-wise so `app.safetyReportFoo` never matches root `app.safetyReport`.
const isUnderRoot = (name: string, root: string): boolean =>
  name === root || name.startsWith(`${root}.`);

const syntheticGroupFor = (name: string): SyntheticGroup | null =>
  SYNTHETIC_GROUPS.find((g) => g.roots.some((r) => isUnderRoot(name, r))) ?? null;

// Detect a unit hint from the canonical name's last segment.
const unitFor = (name: string): string | null => {
  const tail = name.split('.').pop() ?? '';
  if (/Minutes$/.test(tail)) return 'min';
  if (/Seconds$/.test(tail)) return 'sec';
  if (/Milliseconds$|Ms$/.test(tail)) return 'ms';
  if (/Hours$/.test(tail)) return 'hr';
  if (/Days$/.test(tail)) return 'days';
  if (/Meters$|Radius$/.test(tail)) return 'm';
  if (/Percent$|Percentage$/.test(tail)) return '%';
  return null;
};

// Turn enum constants like EXPIRED_ESCALATED into sentence case.
const prettyEnumLabel = (raw: string): string => {
  if (!raw) return raw;
  const spaced = raw.replace(/_/g, ' ').toLowerCase();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

export const EntityOverrideEditor: React.FC<EntityOverrideEditorProps> = ({
  level,
  entityId,
  effectiveParams,
  header,
  emptyStateMessage,
}) => {
  const [catalog, setCatalog] = useState<Record<string, CatalogEntry>>({});
  const [effective, setEffective] = useState<EffectiveResponse | null>(null);
  const [drafts, setDrafts] = useState<Record<string, RowDraft>>({});
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [savingRow, setSavingRow] = useState<string | null>(null);
  // Staged "lock/unlock entire feature" changes (prefix → desired mode),
  // committed together with per-row edits via Save all. Feature locks used to
  // write instantly while row edits staged — this makes the whole editor use
  // one consistent save model.
  const [pendingFeatureLocks, setPendingFeatureLocks] = useState<Record<string, 'locked-subtree' | 'open'>>({});
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const effectiveReqIdRef = useRef(0);

  // Load catalog once.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const catalogRes = await v2SettingsApi.getCatalog();
        if (cancelled) return;
        setCatalog(catalogRes?.settings ?? {});
      } catch (err: any) {
        if (!cancelled) setError(extractApiError(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Serialize effectiveParams for the dependency array; React can't
  // shallow-compare object literals.
  const paramsKey = JSON.stringify(effectiveParams);

  // `keepError`: the refetch after a failed Save all must not clear the failure
  // message it is about to display — it used to, so a rejected value vanished
  // with no visible error at all (ticket 868m81zbq).
  const loadEffective = async (opts: { keepError?: boolean } = {}) => {
    const myReqId = ++effectiveReqIdRef.current;
    if (!entityId) {
      setEffective(null);
      setDrafts({});
      setPendingFeatureLocks({});
      return;
    }
    setRefreshing(true);
    if (!opts.keepError) setError(null);
    try {
      const r = await v2SettingsApi.getEffective(effectiveParams);
      if (myReqId !== effectiveReqIdRef.current) return; // stale
      setEffective(r);
      const d: Record<string, RowDraft> = {};
      for (const [name, res] of Object.entries(r.resolved ?? {})) {
        d[name] = {
          value: res.value,
          valueText: toText(res.value),
          policyMode: (res.lockedBy === level ? 'locked' : NO_POLICY),
          policyReason: res.reason || '',
        };
      }
      setDrafts(d);
      setPendingFeatureLocks({});
    } catch (err: any) {
      if (myReqId === effectiveReqIdRef.current) setError(extractApiError(err));
    } finally {
      if (myReqId === effectiveReqIdRef.current) setRefreshing(false);
    }
  };

  useEffect(() => { void loadEffective(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [entityId, paramsKey]);

  const editableEntries = useMemo(
    () => Object.entries(catalog)
      .filter(([, def]) => def.allowedLevels.includes(level))
      .sort(([a], [b]) => a.localeCompare(b)),
    [catalog, level],
  );

  const groupedEntries = useMemo(() => {
    type Bucket = {
      label: string;
      roots: string[];
      masterName: string;
      synthetic: boolean;
      summary?: string;
      master: [string, CatalogEntry] | null;
      rows: Array<[string, CatalogEntry]>;
    };
    const groups = new Map<string, Bucket>();
    for (const entry of editableEntries) {
      const [name] = entry;
      const synthetic = syntheticGroupFor(name);
      const prefix = name.split('.')[0];
      const key = synthetic ? synthetic.key : prefix;
      let g = groups.get(key);
      if (!g) {
        g = synthetic
          ? { label: synthetic.label, roots: synthetic.roots, masterName: synthetic.master, synthetic: true, summary: synthetic.summary, master: null, rows: [] }
          : { label: prettifyPrefix(prefix), roots: [prefix], masterName: `${prefix}.enabled`, synthetic: false, master: null, rows: [] };
        groups.set(key, g);
      }
      if (name === g.masterName) g.master = entry;
      else g.rows.push(entry);
    }
    return Array.from(groups.entries())
      .map(([key, b]) => ({ key, label: b.label, roots: b.roots, synthetic: b.synthetic, summary: b.summary, master: b.master, rows: b.rows }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [editableEntries]);

  // A feature lock is a subtree policy write. Normal groups own one subtree;
  // a synthetic group owns several, so locking it writes each root.
  const lockRootsForGroup = (key: string): string[] =>
    groupedEntries.find((g) => g.key === key)?.roots ?? [key];

  // Heuristic: a feature is "fully locked at this level" if every row
  // in the group shows lockedBy === this level.
  const isFeatureFullyLocked = (groupKey: string): boolean => {
    const group = groupedEntries.find((g) => g.key === groupKey);
    if (!group) return false;
    const all = [...(group.master ? [group.master] : []), ...group.rows];
    if (all.length === 0) return false;
    return all.every(([name]) => effective?.resolved?.[name]?.lockedBy === level);
  };

  const getFeatureStatus = (group: typeof groupedEntries[number]): FeatureStatus => {
    if (!group.master) return 'no-master';
    const [masterName] = group.master;
    const res = effective?.resolved?.[masterName];
    if (res?.value) return 'on';
    return res?.lockedBy === level ? 'off-locked' : 'off-unlocked';
  };

  // Reset expand state on entity change.
  useEffect(() => { setExpanded({}); }, [entityId]);

  // Initialize expand state for groups we haven't seen yet (preserves user
  // toggles across /effective refetches).
  useEffect(() => {
    if (!effective) return;
    setExpanded((prev) => {
      const next = { ...prev };
      let changed = false;
      for (const g of groupedEntries) {
        if (next[g.key] === undefined) {
          const masterName = g.master?.[0];
          const masterValue = masterName ? effective.resolved?.[masterName]?.value : undefined;
          next[g.key] = !!masterValue;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [effective, groupedEntries]);

  const updateDraft = (name: string, patch: Partial<RowDraft>) => {
    setDrafts((prev) => ({ ...prev, [name]: { ...prev[name], ...patch } }));
  };

  // Dirty checks use the per-level current policy mode — `lockedBy === level`
  // means "locked at this level", which is the only state the dropdown can
  // represent (locks from above are read-only and surfaced as "Locked by X").
  const policyAdapter = (resolved: EffectiveResponse['resolved'][string] | undefined) => {
    if (!resolved) return undefined;
    return {
      ...resolved,
      // Only treat as `locked` for dirty detection when this level set it.
      // Locks from higher levels aren't toggled by this editor.
      lockedBy: resolved.lockedBy === level ? level : undefined,
    };
  };

  const isValueDirty = (name: string, def: CatalogEntry) =>
    isValueDirtyPure(drafts[name], effective?.resolved?.[name], def);
  const isPolicyDirty = (name: string, def: CatalogEntry) =>
    isPolicyDirtyPure(drafts[name], policyAdapter(effective?.resolved?.[name]), def);
  const isRowDirty = (name: string, def: CatalogEntry) =>
    isRowDirtyPure(drafts[name], policyAdapter(effective?.resolved?.[name]), def);

  const toggleClear = (name: string, scope: 'value' | 'policy') => {
    const draft = drafts[name];
    if (!draft) return;
    if (scope === 'value') {
      updateDraft(name, { pendingClearValue: !draft.pendingClearValue });
    } else {
      updateDraft(name, { pendingClearPolicy: !draft.pendingClearPolicy });
    }
  };

  const dirtyRows = useMemo(
    () => editableEntries.filter(([name, def]) => isRowDirty(name, def)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [editableEntries, drafts, effective],
  );

  const applyRow = async (name: string, def: CatalogEntry) => {
    if (!entityId) return;
    const scope = deleteScopeForPure(drafts[name]);
    if (scope) {
      await v2SettingsApi.deleteEntityOverride(level, entityId, name, scope);
    }
    const body = buildPatchBodyPure(drafts[name], policyAdapter(effective?.resolved?.[name]), def);
    if (Object.keys(body).length > 0) {
      await v2SettingsApi.patchEntityOverride(level, entityId, name, body);
    }
  };

  const pendingFeatureLockCount = Object.keys(pendingFeatureLocks).length;
  const totalPending = dirtyRows.length + pendingFeatureLockCount;

  const saveAll = async () => {
    const featureLockEntries = Object.entries(pendingFeatureLocks) as Array<[string, 'locked-subtree' | 'open']>;
    if (!entityId || (dirtyRows.length === 0 && featureLockEntries.length === 0)) return;
    // Validate every dirty row client-side first. A bad value used to go to
    // the server, fail, and be wiped by the refetch below — which looked
    // exactly like a successful save until the field came back empty
    // (ticket 868m81zbq).
    const invalid = dirtyRows
      .map(([name, def]) => ({ name, problem: validateDraftValue(drafts[name], def) }))
      .filter((r): r is { name: string; problem: string } => !!r.problem);
    if (invalid.length > 0) {
      const msg = `${invalid.length} value${invalid.length === 1 ? '' : 's'} can't be saved. ${invalid[0].name}: ${invalid[0].problem}`;
      setInfo(null);
      setError(msg);
      toast.error(msg);
      return;
    }
    setSavingRow(SAVE_ALL_KEY);
    setError(null);
    setInfo(null);
    try {
      // Feature-wide locks are subtree policy writes that cover every setting
      // beneath a root, so apply them FIRST — that way an explicit per-row
      // policy staged for a setting in the same feature is written last and
      // wins over the blanket lock (matches the pre-staging behavior, where a
      // per-row save after an instant feature lock was the last write).
      // A synthetic group spans several roots, so one staged lock can be more
      // than one write; they run in sequence so a failure part-way through
      // reports against the group rather than silently half-applying.
      const featureFailures = await saveAllRows(
        featureLockEntries,
        async (groupKey, mode) => {
          for (const root of lockRootsForGroup(groupKey)) {
            await v2SettingsApi.patchEntityOverride(level, entityId, root, { policy: { mode } });
          }
        },
      );
      const rowFailures = await saveAllRows(dirtyRows, applyRow);
      const failures = [...featureFailures, ...rowFailures];
      if (failures.length === 0) {
        setInfo(`Saved ${totalPending} change${totalPending === 1 ? '' : 's'}`);
      } else {
        const msg = `${failures.length} of ${totalPending} saves failed (first: ${failures[0].name} — ${extractApiError(failures[0].error)})`;
        setError(msg);
        // The alert renders at the top of a long page while the Save bar is
        // pinned to the bottom, so also surface it where the user is looking.
        toast.error(msg);
      }
      // Refetch the committed state, but keep the drafts of rows that failed so
      // the user can correct them instead of finding the field reset.
      const keep: Record<string, RowDraft> = {};
      for (const f of failures) if (drafts[f.name]) keep[f.name] = drafts[f.name];
      await loadEffective({ keepError: failures.length > 0 });
      if (Object.keys(keep).length > 0) setDrafts((prev) => ({ ...prev, ...keep }));
    } finally {
      setSavingRow(null);
    }
  };

  const discardAll = async () => {
    if (!entityId) return;
    setError(null);
    setInfo(null);
    setPendingFeatureLocks({});
    await loadEffective();
  };

  // Committed feature-lock mode from the server for a group.
  const committedFeatureMode = (groupKey: string): 'locked-subtree' | 'open' =>
    isFeatureFullyLocked(groupKey) ? 'locked-subtree' : 'open';

  // Effective mode shown in the UI = staged value if any, else committed.
  const featureLockMode = (groupKey: string): 'locked-subtree' | 'open' =>
    pendingFeatureLocks[groupKey] ?? committedFeatureMode(groupKey);

  const isFeatureLockStaged = (groupKey: string): boolean =>
    pendingFeatureLocks[groupKey] !== undefined;

  // Stage (don't write) a feature-wide lock/unlock. Staging the value that
  // already matches the server un-stages it, so toggling back and forth leaves
  // nothing pending.
  const stageFeatureLock = (groupKey: string, mode: 'locked-subtree' | 'open') => {
    setError(null);
    setInfo(null);
    setPendingFeatureLocks((prev) => {
      const next = { ...prev };
      if (mode === committedFeatureMode(groupKey)) delete next[groupKey];
      else next[groupKey] = mode;
      return next;
    });
  };

  const renderValueInput = (name: string, def: CatalogEntry, disabledByLockFromAbove: boolean) => {
    const draft = drafts[name];
    if (!draft) return null;
    const set = (v: unknown) => updateDraft(name, { value: v });
    const valueDisabled = !!draft.pendingClearValue || disabledByLockFromAbove;
    const wrapDimmed = (node: React.ReactNode) =>
      valueDisabled ? <Box sx={{ opacity: 0.5 }}>{node}</Box> : <>{node}</>;

    switch (def.type) {
      case 'boolean': {
        const on = !!draft.value;
        return wrapDimmed(
          <ToggleButtonGroup
            size="small"
            exclusive
            disabled={valueDisabled}
            value={on ? 'on' : 'off'}
            onChange={(_, v) => v !== null && set(v === 'on')}
          >
            <ToggleButton value="on" sx={{ px: 2, py: 0.25, fontWeight: 600, '&.Mui-selected': { bgcolor: 'success.main', color: 'common.white', '&:hover': { bgcolor: 'success.dark' } } }}>ON</ToggleButton>
            <ToggleButton value="off" sx={{ px: 2, py: 0.25, fontWeight: 600, '&.Mui-selected': { bgcolor: 'grey.500', color: 'common.white', '&:hover': { bgcolor: 'grey.600' } } }}>OFF</ToggleButton>
          </ToggleButtonGroup>,
        );
      }
      case 'integer':
      case 'number': {
        const unit = unitFor(name);
        return wrapDimmed(
          <TextField
            size="small"
            type="number"
            disabled={valueDisabled}
            value={draft.value === undefined || draft.value === null ? '' : String(draft.value)}
            onChange={(e) => {
              const v = e.target.value;
              if (v === '') return set(undefined);
              const n = def.type === 'integer' ? parseInt(v, 10) : Number(v);
              if (Number.isFinite(n)) set(n);
            }}
            inputProps={{ min: def.min, max: def.max, step: def.type === 'integer' ? 1 : 'any' }}
            InputProps={unit ? { endAdornment: <InputAdornment position="end"><Typography variant="caption" color="text.secondary">{unit}</Typography></InputAdornment> } : undefined}
            sx={{ width: 160 }}
          />,
        );
      }
      case 'enum': {
        const current = typeof draft.value === 'string' ? draft.value : '';
        const options = def.enum ?? [];
        const isLegacy = current !== '' && !options.includes(current);
        return wrapDimmed(
          <FormControl size="small" sx={{ minWidth: 200 }} disabled={valueDisabled}>
            <Select
              value={current}
              onChange={(e) => set(e.target.value)}
              renderValue={(v) => prettyEnumLabel(String(v))}
            >
              {options.map((opt) => (
                <MenuItem key={opt} value={opt}>{prettyEnumLabel(opt)}</MenuItem>
              ))}
              {isLegacy && (
                <MenuItem key={current} value={current} disabled>
                  {prettyEnumLabel(current)} (legacy)
                </MenuItem>
              )}
            </Select>
          </FormControl>,
        );
      }
      case 'string':
        return wrapDimmed(
          <TextField
            size="small"
            fullWidth
            disabled={valueDisabled}
            value={typeof draft.value === 'string' ? draft.value : (draft.value == null ? '' : String(draft.value))}
            onChange={(e) => set(e.target.value)}
          />,
        );
      case 'stringArray':
        // A real list editor — the raw-JSON textarea it replaces silently lost
        // anything typed without JSON array syntax (ticket 868m81zbq).
        return wrapDimmed(
          <StringArrayField
            name={name}
            def={def}
            valueText={draft.valueText}
            disabled={valueDisabled}
            onChange={(valueText) => updateDraft(name, { valueText })}
          />,
        );
      case 'object':
      default: {
        const problem = validateDraftValue(draft, def);
        return wrapDimmed(
          <TextField
            size="small"
            fullWidth
            multiline
            minRows={2}
            disabled={valueDisabled}
            value={draft.valueText}
            onChange={(e) => updateDraft(name, { valueText: e.target.value })}
            placeholder="{ }"
            error={!!problem}
            helperText={problem ?? undefined}
          />,
        );
      }
    }
  };

  if (loading) return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>;

  const sourceColor = (source?: string): 'default' | 'primary' | 'secondary' | 'info' => {
    if (source === level) return 'primary';
    if (source === 'system') return 'default';
    if (source === 'customer') return 'info';
    if (source === 'group') return 'secondary';
    return 'default';
  };

  return (
    <Box>
      {header}

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {info && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setInfo(null)}>{info}</Alert>}

      {!entityId ? (
        <Typography color="text.secondary">{emptyStateMessage ?? `Pick a ${level} to view and edit overrides.`}</Typography>
      ) : (
        <>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Showing {editableEntries.length} {level}-editable settings across {groupedEntries.length} feature{groupedEntries.length === 1 ? '' : 's'} · environment: <code>{effective?.environment}</code>
            </Typography>
            {refreshing && <CircularProgress size={14} />}
          </Stack>

          <Slide direction="up" in={totalPending > 0} mountOnEnter unmountOnExit>
            <Paper
              elevation={8}
              sx={{
                position: 'fixed',
                bottom: 24,
                left: '50%',
                transform: 'translateX(-50%)',
                zIndex: (t) => t.zIndex.snackbar,
                px: 2,
                py: 1.25,
                borderRadius: 2,
                minWidth: 360,
              }}
            >
              <Stack direction="row" alignItems="center" spacing={2} divider={<Divider orientation="vertical" flexItem />}>
                <Typography variant="body2">
                  <b>{totalPending}</b> unsaved change{totalPending === 1 ? '' : 's'}
                </Typography>
                <Stack direction="row" spacing={1}>
                  <Button size="small" variant="text" disabled={savingRow === SAVE_ALL_KEY} onClick={() => void discardAll()}>
                    Discard
                  </Button>
                  <Button size="small" variant="contained" disabled={savingRow === SAVE_ALL_KEY} onClick={() => void saveAll()}>
                    {savingRow === SAVE_ALL_KEY ? 'Saving…' : 'Save all'}
                  </Button>
                </Stack>
              </Stack>
            </Paper>
          </Slide>

          <Stack spacing={1}>
            {groupedEntries.map((group) => {
              const isOpen = !!expanded[group.key];
              // Committed state drives the summary chips; effective (staged or
              // committed) drives the lock button so it previews the pending result.
              const featureLockedCommitted = isFeatureFullyLocked(group.key);
              const featureLocked = featureLockMode(group.key) === 'locked-subtree';
              const featureLockPending = isFeatureLockStaged(group.key);
              const rowCount = (group.master ? 1 : 0) + group.rows.length;
              const status = getFeatureStatus(group);
              return (
                <Accordion
                  key={group.key}
                  variant="outlined"
                  disableGutters
                  expanded={isOpen}
                  onChange={(_, v) => setExpanded((prev) => ({ ...prev, [group.key]: v }))}
                  sx={{ '&:before': { display: 'none' } }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{ bgcolor: 'grey.50', '& .MuiAccordionSummary-content': { alignItems: 'center', gap: 1, my: 0.5 } }}
                  >
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      {group.label}
                    </Typography>
                    {/* A synthetic group spans several unrelated prefixes, so a
                        single `prefix.*` caption would be a lie — the rows carry
                        the full canonical names instead. */}
                    {!group.synthetic && (
                      <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                        {group.roots[0]}.*
                      </Typography>
                    )}
                    <Chip size="small" label={`${rowCount} setting${rowCount === 1 ? '' : 's'}`} variant="outlined" />
                    {status === 'on' && (
                      <Chip size="small" color="success" label="Feature ON" />
                    )}
                    {status === 'off-unlocked' && (
                      <Tooltip title="Feature is OFF, but sub-orgs below can override and turn it on." arrow placement="top">
                        <Chip size="small" variant="outlined" label="Feature OFF · sub-orgs may enable" />
                      </Tooltip>
                    )}
                    {status === 'off-locked' && (
                      <Tooltip title="Feature is OFF and locked at this level — no one below can turn it on." arrow placement="top">
                        <Chip size="small" color="warning" icon={<LockOutlinedIcon />} label="Feature OFF · locked" />
                      </Tooltip>
                    )}
                    {featureLockedCommitted && status !== 'off-locked' && (
                      <Chip size="small" color="warning" icon={<LockOutlinedIcon />} label="Locked for sub-orgs" />
                    )}
                    <Box sx={{ flex: 1 }} />
                    {featureLockPending && (
                      <Chip
                        size="small"
                        color="primary"
                        variant="outlined"
                        label="unsaved"
                        sx={{ height: 20, '& .MuiChip-label': { px: 0.75, fontSize: 10.5 } }}
                      />
                    )}
                    <Tooltip
                      title={
                        featureLocked
                          ? `Unlock — allow sub-orgs to override settings in this feature. Applied when you Save all.`
                          : `Lock — prevent sub-orgs from overriding any ${group.roots.map((r) => `${r}.*`).join(', ')} setting. Applied when you Save all.`
                      }
                      arrow
                      placement="left"
                    >
                      <span>
                        <Button
                          size="small"
                          variant={featureLocked ? 'outlined' : 'text'}
                          color={featureLocked ? 'warning' : 'inherit'}
                          startIcon={featureLocked ? <LockOpenOutlinedIcon /> : <LockOutlinedIcon />}
                          disabled={savingRow === SAVE_ALL_KEY}
                          onClick={(e) => {
                            e.stopPropagation();
                            stageFeatureLock(group.key, featureLocked ? 'open' : 'locked-subtree');
                          }}
                        >
                          {featureLocked ? 'Unlock entire feature' : 'Lock entire feature'}
                        </Button>
                      </span>
                    </Tooltip>
                  </AccordionSummary>
                  <AccordionDetails sx={{ p: 0 }}>
                    {group.summary && (
                      <Typography
                        variant="body2"
                        sx={{
                          px: 2, py: 1.25,
                          bgcolor: (t) => alpha(t.palette.warning.main, 0.08),
                          borderBottom: 1, borderColor: 'divider', color: 'warning.dark',
                        }}
                      >
                        {group.summary}
                      </Typography>
                    )}
                    <Table size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ width: '34%' }}>Setting</TableCell>
                          <TableCell sx={{ width: '10%' }}>Source</TableCell>
                          <TableCell sx={{ width: '16%' }}>Value</TableCell>
                          <TableCell sx={{ width: '40%' }}>
                            <Tooltip
                              title={`"Same for everyone" forces this value on every level below this ${level}. "Sub-orgs can adjust" makes it a starting value the levels the setting supports can override — how far is shown beneath, from the catalog.`}
                              arrow
                              placement="top"
                            >
                              <Stack direction="row" alignItems="center" spacing={0.5} sx={{ display: 'inline-flex' }}>
                                <span>Who can change it</span>
                                <InfoOutlinedIcon fontSize="inherit" sx={{ color: 'text.secondary', cursor: 'help' }} />
                              </Stack>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {group.master && renderSettingRow(group.master[0], group.master[1], true, status)}
                        {group.rows.map(([name, def]) => renderSettingRow(name, def, false, status))}
                      </TableBody>
                    </Table>
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </Stack>
        </>
      )}
    </Box>
  );

  function renderSettingRow(name: string, def: CatalogEntry, isMaster: boolean, status: FeatureStatus) {
    const resolved = effective?.resolved?.[name];
    const draft = drafts[name];
    const dirty = isRowDirty(name, def);
    const saveAllInFlight = savingRow === SAVE_ALL_KEY;

    // A row is locked from above when /effective reports lockedBy at a level
    // higher than the current editor's. Such rows are read-only here — the
    // lock can only be cleared by the level that set it.
    const lockedByAbove = !!resolved?.lockedBy && resolved.lockedBy !== level;

    const note = noteFor(name);
    const dim = !isMaster && status === 'off-unlocked' ? 0.7
              : !isMaster && status === 'off-locked'   ? 0.5
              : 1;

    return (
      <TableRow
        key={name}
        hover
        sx={{
          ...(isMaster ? { backgroundColor: 'action.hover' } : {}),
          ...(dirty ? {
            backgroundColor: 'rgba(25,118,210,0.06)',
            borderLeft: '3px solid',
            borderLeftColor: 'primary.main',
          } : {}),
          opacity: dim,
        }}
      >
        <TableCell sx={{ fontFamily: 'monospace', verticalAlign: 'top' }}>
          {/* The table is `tableLayout: fixed` and this column is 34%, while a
              canonical name is one unbroken token with no spaces to wrap at —
              long ones such as
              `safetyHub.safetyReport.enhancedToIntakeRiskAssessment.addressOnly`
              overflowed the cell and were visually cut off. `minWidth: 0` lets
              the flex item shrink below its content width and `overflowWrap`
              lets the token break mid-word so the whole name stays readable. */}
          <Stack direction="row" alignItems="flex-start" spacing={0.5} sx={{ minWidth: 0 }}>
            {isMaster && (
              <Tooltip title="Master toggle — enables or disables this entire feature" arrow placement="top">
                <StarIcon fontSize="inherit" sx={{ color: 'warning.main', mt: '3px', flex: '0 0 auto' }} />
              </Tooltip>
            )}
            <Typography
              variant="body2"
              sx={{ fontFamily: 'monospace', fontWeight: isMaster ? 600 : 400, minWidth: 0, overflowWrap: 'anywhere' }}
            >
              {name}
            </Typography>
          </Stack>
          {def.description && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              {def.description}
            </Typography>
          )}
          {note && (
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                mt: 0.5,
                pl: 0.75,
                borderLeft: '2px solid',
                borderLeftColor: note.tone === 'required' ? 'warning.main' : 'divider',
                color: note.tone === 'required' ? 'warning.dark' : 'text.secondary',
                fontWeight: note.tone === 'required' ? 600 : 400,
                fontFamily: (t) => t.typography.body1.fontFamily,
              }}
            >
              {note.text}
            </Typography>
          )}
          {lockedByAbove && (
            <Typography variant="caption" sx={{ display: 'block', color: 'warning.dark', mt: 0.5 }}>
              🔒 Locked at {resolved.lockedBy} level{resolved.reason ? ` — ${resolved.reason}` : ''}
            </Typography>
          )}
        </TableCell>
        <TableCell sx={{ verticalAlign: 'top' }}>
          <Chip
            size="small"
            variant="outlined"
            label={resolved?.source ?? 'system'}
            color={sourceColor(resolved?.source)}
          />
        </TableCell>
        <TableCell sx={{ verticalAlign: 'top' }}>
          <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
            {renderValueInput(name, def, lockedByAbove)}
            {dirty && (
              <Tooltip
                title={(draft?.pendingClearValue || draft?.pendingClearPolicy) ? 'Will clear on save' : 'Unsaved change'}
                arrow
              >
                <Chip
                  size="small"
                  color={(draft?.pendingClearValue || draft?.pendingClearPolicy) ? 'warning' : 'primary'}
                  variant="outlined"
                  label={(draft?.pendingClearValue || draft?.pendingClearPolicy) ? 'will clear' : 'edited'}
                  sx={{ height: 20, '& .MuiChip-label': { px: 0.75, fontSize: 10.5 } }}
                />
              </Tooltip>
            )}
          </Stack>
          {resolved?.source === level && !lockedByAbove && (
            <Button
              size="small"
              variant="text"
              color="warning"
              disabled={saveAllInFlight}
              onClick={() => toggleClear(name, 'value')}
              sx={{ mt: 0.5, p: 0, minWidth: 0, fontSize: 11.5, textTransform: 'none' }}
            >
              {draft?.pendingClearValue ? 'Cancel clear' : 'Clear value'}
            </Button>
          )}
        </TableCell>
        <TableCell sx={{ verticalAlign: 'top' }}>
          {renderWhoDecides(name, def, lockedByAbove)}
          {resolved?.lockedBy === level && (
            <Button
              size="small"
              variant="text"
              color="warning"
              disabled={saveAllInFlight}
              onClick={() => toggleClear(name, 'policy')}
              sx={{ mt: 0.5, p: 0, minWidth: 0, fontSize: 11.5, textTransform: 'none' }}
            >
              {draft?.pendingClearPolicy ? 'Cancel clear' : 'Clear lock'}
            </Button>
          )}
        </TableCell>
      </TableRow>
    );
  }

  // The reach chain: current level + descendants, dots/labels/connectors
  // colored by each node's derived state. "Shown, not chosen" — how far Open
  // travels is dictated by the setting's allowedLevels, not picked here.
  function renderReachChain(chain: ReturnType<typeof deriveReach>['chain']) {
    return (
      <Stack direction="row" alignItems="center" flexWrap="nowrap" sx={{ mt: 1 }}>
        {chain.map((node) => {
          const dotSx =
            node.state === 'current' ? { width: 11, height: 11, bgcolor: 'primary.main' }
            : node.state === 'reach' ? { width: 9, height: 9, bgcolor: 'primary.main' }
            : node.state === 'frozen' ? { width: 9, height: 9, bgcolor: 'grey.400' }
            : { width: 8, height: 8, bgcolor: 'background.paper', border: '1.5px dashed', borderColor: 'grey.400' };
          const labelColor =
            node.state === 'current' ? 'text.primary'
            : node.state === 'reach' ? 'primary.main'
            : node.state === 'frozen' ? 'text.disabled'
            : 'text.disabled';
          return (
            <React.Fragment key={node.level}>
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Box sx={{ borderRadius: '50%', flex: '0 0 auto', ...dotSx }} />
                <Typography variant="caption" sx={{ color: labelColor, fontWeight: node.state === 'current' ? 700 : 500 }}>
                  {node.label}
                </Typography>
                {node.lock && <LockOutlinedIcon sx={{ fontSize: 12, color: 'warning.main' }} />}
              </Stack>
              {node.showConn && (
                <Box sx={{ width: 14, height: '2px', mx: 0.75, flex: '0 0 auto', bgcolor: node.connBright ? 'primary.main' : 'divider' }} />
              )}
            </React.Fragment>
          );
        })}
      </Stack>
    );
  }

  // "Who can change it" cell — the binary Everyone/Sub-orgs control + reach
  // chain + plain caption, adapted from the Claude Design CS concept. Binary
  // selection maps straight to the existing policy model: Everyone = locked,
  // Sub-orgs can adjust = open (NO_POLICY).
  function renderWhoDecides(name: string, def: CatalogEntry, lockedByAbove: boolean) {
    const draft = drafts[name];
    const resolved = effective?.resolved?.[name];
    const lockedHere = draft?.policyMode === 'locked';
    // Value lives in its own column now, so the caption stays value-agnostic
    // ("Same for everyone — …") rather than repeating it.
    const reach = deriveReach({ level, allowedLevels: def.allowedLevels, lockedHere });
    const clearStaged = !!draft?.pendingClearPolicy;

    // Locked by a higher level → read-only here.
    if (lockedByAbove) {
      return (
        <Typography variant="caption" color="text.secondary">
          Controlled at {resolved?.lockedBy} level
        </Typography>
      );
    }

    const captionEl = (
      <Stack direction="row" alignItems="flex-start" spacing={0.75} sx={{ mt: 1 }}>
        <Box sx={{ width: 6, height: 6, borderRadius: '50%', mt: '5px', flex: '0 0 auto', bgcolor: reach.captionTone === 'brand' ? 'primary.main' : 'text.disabled' }} />
        <Typography variant="caption" sx={{ color: reach.captionTone === 'brand' ? 'primary.main' : 'text.secondary', lineHeight: 1.4 }}>
          {reach.caption}
        </Typography>
      </Stack>
    );

    // No descendant level supports this setting → nothing to delegate.
    if (!reach.canDelegate) {
      return (
        <Box>
          <Chip size="small" variant="outlined" label="Set here" />
          {renderReachChain(reach.chain)}
          {captionEl}
        </Box>
      );
    }

    // Setting can cascade but can't be locked → always open, no toggle.
    if (!def.lockable) {
      return (
        <Box>
          <Chip size="small" variant="outlined" color="primary" label="Sub-orgs can adjust" />
          {renderReachChain(reach.chain)}
          {captionEl}
        </Box>
      );
    }

    // Lockable + delegable → the binary control.
    return (
      <Box sx={{ opacity: clearStaged ? 0.5 : 1 }}>
        <SegmentedControl
          value={lockedHere ? 'everyone' : 'open'}
          disabled={clearStaged}
          onChange={(v) => updateDraft(name, { policyMode: v === 'everyone' ? 'locked' : NO_POLICY })}
          options={[
            { value: 'everyone', label: 'Same for everyone' },
            { value: 'open', label: 'Sub-orgs can adjust' },
          ]}
        />
        {renderReachChain(reach.chain)}
        {captionEl}
      </Box>
    );
  }
};

/**
 * Compact iOS-style segmented control — grey track, the active segment is a
 * white "thumb" with a soft shadow. Replaces the heavy MUI ToggleButtonGroup
 * for a more modern, less shouty binary control.
 */
const SegmentedControl: React.FC<{
  value: string;
  options: Array<{ value: string; label: string }>;
  onChange: (value: string) => void;
  disabled?: boolean;
}> = ({ value, options, onChange, disabled }) => (
  <Box
    role="tablist"
    sx={{
      display: 'inline-flex',
      p: '3px',
      gap: '2px',
      // Darker track so the white active "thumb" clearly separates from it.
      bgcolor: 'grey.300',
      borderRadius: '10px',
    }}
  >
    {options.map((opt) => {
      const active = opt.value === value;
      return (
        <ButtonBase
          key={opt.value}
          role="tab"
          aria-selected={active}
          disabled={disabled}
          onClick={() => onChange(opt.value)}
          sx={{
            px: 1.75,
            py: 0.6,
            borderRadius: '8px',
            fontSize: 12.5,
            // Active: raised white thumb + bold primary label. Inactive: flat,
            // muted. The two together read unambiguously.
            fontWeight: active ? 700 : 500,
            whiteSpace: 'nowrap',
            transition: 'background-color .15s, color .15s, box-shadow .15s',
            color: active ? 'primary.main' : 'text.secondary',
            bgcolor: active ? 'background.paper' : 'transparent',
            boxShadow: active ? '0 1px 3px rgba(16,24,40,.2), 0 1px 2px rgba(16,24,40,.12)' : 'none',
            '&:hover': { bgcolor: active ? 'background.paper' : 'rgba(255,255,255,.5)' },
          }}
        >
          {opt.label}
        </ButtonBase>
      );
    })}
  </Box>
);
